import React, { useState, useEffect } from "react";
import HeaderNavbar from "./components/HeaderNavbar";
import Footer from "./components/Footer";
import LeadCaptureModal from "./components/LeadCaptureModal";
import { NotificationProvider } from "../hooks/use-notification";

const heroImage =
  "https://www.figma.com/api/mcp/asset/9b53ec9c-ba7d-43b0-b86e-c05ff229fa1b";
const articleImage =
  "https://www.figma.com/api/mcp/asset/ac59c54b-3325-42af-8b7b-00fb3bf2d745";
const callIcon = "/call-Icon.svg";

const Blog2Page = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <NotificationProvider>
      <div className="bg-white min-h-screen flex flex-col" style={{ fontFamily: "Noto Sans, sans-serif" }}>
        <HeaderNavbar onOpenModal={() => setIsModalOpen(true)} />

        <main className="flex flex-col gap-[64px] md:gap-[80px] items-start px-4 sm:px-6 md:px-10 xl:px-16 py-10 md:py-16 w-full">

          {/* Hero */}
          <div className="relative w-full rounded-[16px] overflow-hidden min-h-[320px] md:min-h-[420px] flex items-end p-[32px] md:p-[60px]">
            {/* Background image */}
            <div className="absolute inset-0">
              <img
                src={heroImage}
                alt=""
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to right, rgba(20,27,44,0.85), rgba(66,90,146,0.5))",
                }}
              />
            </div>

            {/* Content */}
            <div className="relative flex flex-col gap-[28px] md:gap-[34px] items-start max-w-[900px]">
              <div className="flex flex-col gap-[4px]">
                <h1
                  className="text-white font-bold leading-tight"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  <span className="block text-[32px] sm:text-[48px] md:text-[60px] xl:text-[72px]">
                    Signals Before Symptoms:
                  </span>
                  <span className="block text-[22px] sm:text-[30px] md:text-[36px] xl:text-[42px]">
                    Engineering a Deterministic Cattle Monitoring System
                  </span>
                </h1>
              </div>
              <div className="flex flex-col gap-[6px]">
                <p
                  className="text-white font-bold text-[16px] sm:text-[20px] md:text-[24px] leading-normal"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  By Rachita Reddy | Firmware Engineer | Vanix — MyBovine.ai
                </p>
                <p
                  className="text-white/80 font-medium text-[14px] sm:text-[16px] md:text-[18px] leading-normal"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  March 27, 2026
                </p>
              </div>
            </div>
          </div>

          {/* Content grid */}
          <div className="flex flex-col lg:flex-row gap-[48px] items-start w-full">

            {/* Article */}
            <div className="flex flex-col w-full lg:flex-1">
              <div className="bg-[#f3f4f5] rounded-[12px] p-[24px] md:p-[32px] flex flex-col gap-[40px]">

                {/* Intro */}
                <div className="flex flex-col gap-[24px]">
                  <h2
                    className="text-[#191c1d] font-bold text-[20px] md:text-[24px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    Why Farm Tech Usually Fails
                  </h2>
                  <div
                    className="text-[#191c1d] font-medium text-[16px] md:text-[18px] leading-[28px] flex flex-col gap-[4px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    <p>
                      If you've ever stepped into a professional dairy farm, you know it's a place that hates
                      electronics. Between the thick concrete walls of the milking parlor, the high humidity of
                      the sheds, and the shifting mass of 500kg animals that act as giant "signal absorbers,"
                      the farm is a chaos-engine.
                    </p>
                    <p className="mt-4">
                      When I joined the Vanix and <strong>MyBovine.ai</strong> project to oversee the firmware for our{" "}
                      <strong>BLE Gateway and Node systems</strong>, I realized that a standard "IoT tracker schema" wouldn't
                      survive here. In this vertical, a missed diagnosis isn't just a technical glitch, it's a
                      "silent thief" that steals a farmer's profit.
                    </p>
                  </div>
                </div>

                {/* Section 1 */}
                <div className="flex flex-col gap-[24px]">
                  <h3
                    className="text-[#191c1d] font-bold text-[20px] md:text-[24px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    1. The Real-Time Foundation: Determinism vs. Best-Effort
                  </h3>
                  <div
                    className="text-[#191c1d] font-medium text-[16px] md:text-[18px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    <p>
                      The most critical decision in our journey was the choice of Operating System. While
                      many startups use basic "best-effort" code, our early versions were built on{" "}
                      <strong>Zephyr RTOS.</strong> In a standard consumer gadget, the processor might "lag" while handling a
                      background task. On a farm, that lag can mean a missed fever spike. By using a{" "}
                      <strong>Real-Time Operating System (RTOS)</strong>, we achieved <strong>Deterministic Execution</strong>. Through{" "}
                      <strong>preemptive multi-threading</strong>, our firmware ensures that when a sensor detects a
                      thermal micro-drift or a movement vector indicating a silent heat, the system
                      prioritizes that alert instantly. It doesn't wait for a cycle; it acts.
                    </p>
                    <p className="mt-4">
                      This architectural DNA is also how we hit our <strong>2-year battery life</strong>. Zephyr allows for
                      granular <strong>Power Management</strong> putting the radio and sensors into "Deep Sleep" for 99%
                      of the time, while the "Watchdog" timers stay alert to wake the system the moment a
                      health event is detected.
                    </p>
                  </div>
                </div>

                {/* Section 2 */}
                <div className="flex flex-col gap-[24px]">
                  <h3
                    className="text-[#191c1d] font-bold text-[20px] md:text-[24px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    2. Collision Avoidance at Scale
                  </h3>
                  <div
                    className="text-[#191c1d] font-medium text-[16px] md:text-[18px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    <p>
                      Imagine a pasture with 100 cows all trying to "shout" their health data to one
                      Gateway at the exact same millisecond. In a standard setup, those radio signals crash —
                      a phenomenon known as <strong>Packet Collision</strong>.
                    </p>
                    <p className="mt-4">
                      To solve this, our engineering team developed a{" "}
                      <strong>Collision-Resistant Broadcast Architecture</strong>. Instead of a random "free-for-all," we implemented a proprietary timing
                      logic that naturally de-synchronizes the transmissions across the herd. It's an elegant,
                      low-power way to ensure our Gateway hears every single "heartbeat" from the field.
                      This clean data stream is what powers the live monitoring at our <strong>Hyderabad site</strong>.
                    </p>
                  </div>
                </div>

                {/* Article Image */}
                <div className="w-full rounded-[12px] overflow-hidden" style={{ height: "280px", minHeight: "220px" }}>
                  <img
                    src={articleImage}
                    alt="Farm monitoring system"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Section 3 */}
                <div className="flex flex-col gap-[24px]">
                  <h3
                    className="text-[#191c1d] font-bold text-[20px] md:text-[24px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    3. From Binary Payloads to Agentic AI
                  </h3>
                  <div
                    className="text-[#191c1d] font-medium text-[16px] md:text-[18px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    <p>
                      As a firmware engineer, I spend my day optimizing <strong>Compact Binary Payloads</strong>. By
                      stripping away every unnecessary bit from the data packets, we reduce <strong>Radio Airtime</strong>.
                      Less time spent transmitting means less battery drain and more "room" in the airwaves
                      for other nodes to talk.
                    </p>
                    <p className="mt-4">
                      But the real magic happens when this high-fidelity data reaches the cloud. It feeds
                      directly into our <strong>Agentic AI</strong>. When you "talk" to <strong>your cattles in real time — real life like</strong>,
                      you aren't getting a canned response. You are getting a natural language summary
                      derived from the exact <strong>accelerometer vectors</strong> and <strong>rumination patterns</strong> my firmware
                      captured just seconds ago.
                    </p>
                  </div>
                </div>

                {/* Section 4 */}
                <div className="flex flex-col gap-[24px]">
                  <h3
                    className="text-[#191c1d] font-bold text-[20px] md:text-[24px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    4. Engineering for "Zero-Loss" Farming
                  </h3>
                  <div
                    className="text-[#191c1d] font-medium text-[16px] md:text-[18px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    <p>
                      Our hardware is <strong>IP67-rated</strong>, fully waterproof and shock-resistant—because we know
                      cows aren't gentle with equipment. However, the real "armor" is our <strong>Self-Healing
                      Firmware</strong>.
                    </p>
                    <p className="mt-4">
                      If a cow wanders beyond the range of the Gateway, the node doesn't just stop. It
                      automatically begins <strong>Data Buffering</strong>, storing critical health events in local memory.
                      The moment she steps back into range, the firmware performs a high-speed
                      "Handshake" and re-syncs the missing data. This ensures a 100% complete health
                      history, with zero gaps in the timeline.
                    </p>
                  </div>
                </div>

                {/* Conclusion */}
                <div className="flex flex-col gap-[24px]">
                  <h3
                    className="text-[#191c1d] font-bold text-[20px] md:text-[24px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    Conclusion: The Data Before the Symptoms
                  </h3>
                  <div
                    className="text-[#191c1d] font-medium text-[16px] md:text-[18px] leading-[28px]"
                    style={{ fontFamily: "Satoshi, sans-serif" }}
                  >
                    <p>
                      At <strong>MyBovine.ai</strong>, we believe the most valuable data is the data that arrives{" "}
                      <strong>before</strong> the symptoms do. We aren't just tracking movement; we are engineering the foundation of
                      modern, proactive agriculture. I am proud to lead the firmware initiatives that turn
                      these "invisible signals" into the security every dairy farmer deserves.
                    </p>
                    <p className="mt-4">
                      <strong>Ready to see the engineering in action?</strong> If you're managing a professional herd and
                      want to move from reactive to proactive, reach out to our team at <strong>sales@vanix.in</strong>.
                      We'd love to walk you through a farm assessment.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-[320px] xl:w-[360px] shrink-0 lg:sticky lg:top-[100px]">
              <div
                className="rounded-[16px] p-[24px] flex flex-col gap-[17px]"
                style={{
                  background:
                    "linear-gradient(128.54deg, #34CC80 2.04%, #0084D1 100.05%)",
                }}
              >
                {/* Icon */}
                {callIcon && (
                  <img src={callIcon} alt="" className="h-[30px] w-auto object-contain object-left" />
                )}
                <h4
                  className="text-white font-bold text-[20px] leading-[28px]"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  Ready to Modernise Your Farm?
                </h4>
                <p
                  className="text-white/80 text-[14px] leading-[22px]"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  We are available 24/7 to assist with your herd management deployment.
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white text-[#00bd7e] font-bold text-[14px] text-center rounded-[12px] px-[16px] py-[12px] w-full hover:opacity-90 transition-opacity"
                  style={{ fontFamily: "Satoshi, sans-serif" }}
                >
                  Schedule a Call
                </button>
              </div>
            </div>

          </div>
        </main>

        <Footer />

        <LeadCaptureModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
    </NotificationProvider>
  );
};

export default Blog2Page;
